if (!customElements.get('build-a-kit')) {
  customElements.define(
    'build-a-kit',
    class BuildAKit extends HTMLElement {
      constructor() {
        super();
        this.maxItems = Number(this.dataset.maxItems || 4);
        this.discountPercent = Number(this.dataset.discountPercent || 0);
        this.installmentCount = Number(this.dataset.installmentCount || 6);
        this.currency = this.dataset.currency || 'CLP';
        this.locale = document.documentElement.lang || 'es-CL';
        this.items = new Map();
        this.products = Array.from(this.querySelectorAll('[data-kit-product]'));
        this.submitButton = this.querySelector('[data-kit-submit]');
        this.countCurrent = this.querySelector('[data-kit-count-current]');
        this.progressFill = this.querySelector('[data-kit-progress-fill]');
        this.selectedList = this.querySelector('[data-kit-selected]');
        this.emptyText = this.querySelector('[data-kit-empty]');
        this.subtotalRow = this.querySelector('[data-kit-subtotal-row]');
        this.subtotal = this.querySelector('[data-kit-subtotal]');
        this.summaryBenefits = this.querySelector('[data-kit-summary-benefits]');
        this.installmentPrice = this.querySelector('[data-kit-installment-price]');
        this.message = this.querySelector('[data-kit-message]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.moneyFormatter = new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          maximumFractionDigits: 0,
        });
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick.bind(this));
        this.render();
      }

      onClick(event) {
        const submitButton = event.target.closest('[data-kit-submit]');
        if (submitButton && this.contains(submitButton)) {
          this.addToCart();
          return;
        }

        const button = event.target.closest('[data-kit-action]');
        if (!button || button.disabled) return;

        const product = button.closest('[data-kit-product]');
        if (!product) return;

        const action = button.dataset.kitAction;
        const variantId = product.dataset.variantId;
        const currentQuantity = this.items.get(variantId)?.quantity || 0;

        if (action === 'increase') {
          this.setQuantity(product, currentQuantity + 1);
        }

        if (action === 'decrease') {
          this.setQuantity(product, currentQuantity - 1);
        }

        if (button.hasAttribute('data-kit-submit')) {
          this.addToCart();
        }
      }

      setQuantity(product, quantity) {
        const productId = product.dataset.productId;
        const productHandle = product.dataset.productHandle;
        const variantId = product.dataset.variantId;
        const variantTitle = product.dataset.variantTitle;
        const variantSku = product.dataset.variantSku;
        const title = product.dataset.productTitle;
        const price = Number(product.dataset.price || 0);
        const url = product.dataset.url;
        const imageUrl = product.dataset.imageUrl;
        const totalQuantity = this.totalQuantity();
        const currentQuantity = this.items.get(variantId)?.quantity || 0;
        const nextQuantity = Math.max(0, quantity);

        if (nextQuantity > currentQuantity && totalQuantity >= this.maxItems) return;

        if (nextQuantity === 0) {
          this.items.delete(variantId);
        } else {
          this.items.set(variantId, {
            productId,
            productHandle,
            variantId,
            variantTitle,
            variantSku,
            title,
            price,
            quantity: nextQuantity,
            url,
            imageUrl,
          });
        }

        this.render();
      }

      totalQuantity() {
        return Array.from(this.items.values()).reduce((total, item) => total + item.quantity, 0);
      }

      subtotalAmount() {
        return Array.from(this.items.values()).reduce((total, item) => total + item.price * item.quantity, 0);
      }

      defaultUnitPrice() {
        const availableProduct = this.products.find((product) => product.dataset.available === 'true');
        return Number(availableProduct?.dataset.price || 0);
      }

      totalAmount() {
        const subtotal = this.subtotalAmount();
        const discountMultiplier = Math.max(0, 100 - this.discountPercent) / 100;
        return Math.round(subtotal * discountMultiplier);
      }

      trackingItems() {
        return Array.from(this.items.values()).map((item, index) => ({
          item_id: item.productId,
          item_name: item.title,
          item_handle: item.productHandle,
          item_variant_id: item.variantId,
          item_variant_title: item.variantTitle,
          item_sku: item.variantSku || undefined,
          price: item.price / 100,
          quantity: item.quantity,
          item_url: item.url,
          item_image_url: item.imageUrl,
          index: index + 1,
        }));
      }

      projectedTotalAmount() {
        const totalQuantity = this.totalQuantity();
        if (totalQuantity === 0) {
          const projectedSubtotal = this.defaultUnitPrice() * this.maxItems;
          const discountMultiplier = Math.max(0, 100 - this.discountPercent) / 100;
          return Math.round(projectedSubtotal * discountMultiplier);
        }

        const projectedSubtotal = (this.subtotalAmount() / totalQuantity) * this.maxItems;
        const discountMultiplier = Math.max(0, 100 - this.discountPercent) / 100;
        return Math.round(projectedSubtotal * discountMultiplier);
      }

      installmentAmount() {
        if (this.installmentCount <= 0) return 0;
        return Math.round(this.totalAmount() / this.installmentCount);
      }

      projectedInstallmentAmount() {
        if (this.installmentCount <= 0) return 0;
        return Math.round(this.projectedTotalAmount() / this.installmentCount);
      }

      render() {
        const totalQuantity = this.totalQuantity();
        const isComplete = totalQuantity === this.maxItems;

        if (this.countCurrent) this.countCurrent.textContent = totalQuantity;
        if (this.progressFill) this.progressFill.style.width = `${(totalQuantity / this.maxItems) * 100}%`;
        if (this.submitButton) this.submitButton.disabled = !isComplete;
        if (this.subtotalRow) this.subtotalRow.classList.toggle('is-incomplete', !isComplete);
        if (this.summaryBenefits) this.summaryBenefits.classList.toggle('is-incomplete', !isComplete);
        if (this.subtotal) {
          const displayTotal = isComplete ? this.totalAmount() : this.projectedTotalAmount();
          this.subtotal.textContent = displayTotal > 0
            ? this.moneyFormatter.format(displayTotal / 100)
            : this.subtotal.dataset.pendingText;
        }
        if (this.installmentPrice) {
          const displayInstallment = isComplete ? this.installmentAmount() : this.projectedInstallmentAmount();
          this.installmentPrice.textContent = displayInstallment > 0
            ? this.moneyFormatter.format(displayInstallment / 100)
            : this.subtotal.dataset.pendingText;
        }

        this.products.forEach((product) => {
          const variantId = product.dataset.variantId;
          const selectedQuantity = this.items.get(variantId)?.quantity || 0;
          const isAvailable = product.dataset.available === 'true';
          const value = product.querySelector('[data-kit-quantity]');
          const decrease = product.querySelector('[data-kit-action="decrease"]');
          const increase = product.querySelector('[data-kit-action="increase"]');

          if (value) value.textContent = selectedQuantity;
          if (decrease) decrease.disabled = !isAvailable || selectedQuantity === 0;
          if (increase) increase.disabled = !isAvailable || totalQuantity >= this.maxItems;
        });

        this.renderSelectedItems();
        this.setMessage(isComplete ? this.dataset.readyMessage : this.dataset.incompleteMessage, false);
      }

      renderSelectedItems() {
        if (!this.selectedList) return;

        this.selectedList.innerHTML = '';

        if (this.items.size === 0) {
          if (this.emptyText) this.selectedList.append(this.emptyText.content.cloneNode(true));
          return;
        }

        this.items.forEach((item) => {
          const li = document.createElement('li');
          li.className = 'build-a-kit__selected-item';

          const title = document.createElement('span');
          title.className = 'build-a-kit__selected-title';
          title.textContent = item.title;

          const quantity = document.createElement('span');
          quantity.className = 'build-a-kit__selected-qty';
          quantity.textContent = `x${item.quantity}`;

          li.append(title, quantity);
          this.selectedList.append(li);
        });
      }

      setMessage(text, isError) {
        if (!this.message) return;
        this.message.textContent = text || '';
        if (isError) {
          this.message.setAttribute('role', 'alert');
        } else {
          this.message.removeAttribute('role');
        }
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('loading', isLoading);
        this.submitButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      }

      addToCart() {
        if (this.totalQuantity() !== this.maxItems || !this.submitButton) return;
        let errorMessage = '';

        const cartItems = Array.from(this.items.values()).map((item) => ({
          id: Number(item.variantId),
          quantity: item.quantity,
        }));

        if (this.cart) {
          this.cart.setActiveElement(this.submitButton);
        }

        this.setLoading(true);
        this.submitButton.disabled = true;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify({
          items: cartItems,
          sections: this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : undefined,
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              errorMessage = response.description || response.message || this.dataset.errorMessage;
              return;
            }

            window.themeTracking?.push('build_a_kit_add_to_cart', {
              context: 'build_a_kit',
              items: this.trackingItems(),
              item_count: this.items.size,
              quantity: this.totalQuantity(),
              subtotal_value: this.subtotalAmount() / 100,
              value: this.totalAmount() / 100,
              discount_percent: this.discountPercent,
              kit_max_items: this.maxItems,
              cart_item_count: response.item_count,
            });

            if (this.cart) {
              this.cart.classList.remove('is-empty');
              this.cart.renderContents(response);
            } else {
              window.location = window.routes.cart_url;
            }
          })
          .catch(() => {
            errorMessage = this.dataset.errorMessage;
          })
          .finally(() => {
            this.setLoading(false);
            this.render();
            if (errorMessage) this.setMessage(errorMessage, true);
          });
      }
    }
  );
}
